// A shared folder link's page, /files/T-012/<key> (the agenda sheets' Files column): anyone with the link, no
// sign-in (Layout lets every visitor through). The folder's files and notes, read only:
//   files      the list as in the folder (FileRow): Open (the preview), Download; no upload, no Delete
//   notes      shown as in the folder, cleaned the same (sanitize.ts); not editable
//   refused    a wrong key, a folder gone, links switched off: one 404 from the server, one message here
// The server checks the key (functions/src/folders/link.ts) and sends no names and no rights (PublicFolder).
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { FolderFile, PublicFolder, PublicFolderFile } from '@pathonline/shared';
import { useMemo, useState } from 'react';
import { getJson, HttpError } from '../api.ts';
import { FilePreview } from './FilePreview.tsx';
import { FileRow } from './FolderModal.tsx';
import { newestFirst, sharedFolderUrl, sharedLinks } from './model.ts';
import { Notes } from './Notes.tsx';
import './folders.css';

/** What the page asks; exported for the tests. */
export const sharedFolderQuery = (nodeId: string, key: string) => ({
  queryKey: ['sharedFolder', nodeId, key],
  queryFn: () => getJson<PublicFolder>(sharedFolderUrl(nodeId, key)),
});

/** A shared file as the list and the preview take it: no uploader, nothing to delete. */
const asFolderFile = (f: PublicFolderFile): FolderFile => ({ ...f, by: '', canDelete: false });

/** The notes are never edited here. */
const noEditing = () => undefined;

export function SharedFolderPage({ nodeId, linkKey }: { nodeId: string; linkKey: string }) {
  const qc = useQueryClient();
  const q = useQuery(sharedFolderQuery(nodeId, linkKey));
  const links = useMemo(() => sharedLinks(nodeId, linkKey), [nodeId, linkKey]);
  /** The file in the preview, by id: the list read again may reorder it. */
  const [viewing, setViewing] = useState<string | null>(null);
  const files = useMemo(() => (q.data ? newestFirst(q.data.files.map(asFolderFile)) : []), [q.data]);
  // A file gone from the list (deleted meanwhile) closes the preview.
  const shown = viewing ? files.findIndex((x) => x.id === viewing) : -1;
  // As the folder: Download, or closing the preview, reads the list again, so a file deleted meanwhile leaves it.
  const reload = () => void qc.invalidateQueries({ queryKey: sharedFolderQuery(nodeId, linkKey).queryKey });

  if (q.isPending) return <p className="muted pad">Loading…</p>;
  // A reload that fails keeps the list read before, as the folder does; a 404 means the link itself stopped.
  const gone = q.isError && q.error instanceof HttpError && q.error.status === 404;
  if (q.isError && !gone && !q.data) return <p className="error pad">{q.error.message}</p>;
  if (gone || !q.data) {
    return (
      <div className="sf">
        <h1>This link does not work</h1>
        <p>Ask whoever sent it for a new one.</p>
      </div>
    );
  }
  const f = q.data;
  return (
    <section className="sf" aria-label={`Files of ${f.label}`}>
      <div className="fm-hd">
        <p className="fm-kicker">Shared folder · {f.nodeId}</p>
        <h1 title={f.label}>Files · {f.label}</h1>
      </div>
      <div className="fm-body">
        {q.isError && <p className="error">{q.error.message}</p>}
        <section className="fm-files" aria-label="Files">
          {files.length === 0 ? (
            <p className="fm-none">No files in this folder yet.</p>
          ) : (
            <ul className="fm-list">
              {files.map((x) => (
                <FileRow key={x.id} links={links} file={x} busy={false} onOpen={() => setViewing(x.id)} onFollow={reload} />
              ))}
            </ul>
          )}
        </section>
        {f.notes.html && <Notes nodeId={nodeId} notes={{ html: f.notes.html, at: f.notes.at, etag: '', by: '' }} canEdit={false} editing={false} setEditing={noEditing} />}
      </div>
      {shown >= 0 && (
        <FilePreview
          nodeId={nodeId}
          links={links}
          files={files}
          index={shown}
          onIndex={(i) => setViewing(files[i]!.id)}
          onClose={() => {
            setViewing(null);
            reload();
          }}
        />
      )}
    </section>
  );
}
